import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useIntl } from 'react-intl';
import { useAuth } from "../../AuthContext";
import { supabase } from '../../supabase';
import { semantic } from '../../ui/theme';
import { useSplash } from '../../ui/splash/SplashContext';
import { releaseSignInCoverAfterPaint } from '../../ui/splash/signInCover';
import { withTimeout } from '../../utils/async/withTimeout';
import { shouldForceMfaEnrollment, getEffectiveMfaRequiredRoles } from '../../utils/auth/forcedMfaEnrollment';
import { readEnrollmentBaseline, writeEnrollmentBaseline, enforceableRolesAtSession } from '../../utils/auth/mfaEnrollmentBaseline';
import { readMfaGate, writeMfaGate, clearMfaGate, mfaGateGeneration } from '../../utils/auth/mfaGateCache';
import useMfaServerRecheck from '../../hooks/auth/useMfaServerRecheck';
import TenantMfaEnrollmentGate from '../auth/TenantMfaEnrollmentGate';
import AccessStatusScreen from '../auth/AccessStatusScreen';
import SuspendedAccessGate from './SuspendedAccessGate';
import OpenPlanBillingHandler from './OpenPlanBillingHandler';

const MFA_GATE_TIMEOUT_MS = 12000;

// KAN-529: never throws (a missing RPC / older DB / mocked client → { data: null },
// and the client rule decides alone — fail open to the pre-KAN-529 behavior;
// the server still enforces).
async function fetchServerMfaRequirement() {
  try {
    const res = await supabase.rpc('get_my_mfa_requirement');
    return res?.error ? { data: null } : { data: res?.data ?? null };
  } catch {
    return { data: null };
  }
}

const skipLoginMfa =
  process.env.NODE_ENV === 'development' &&
  (process.env.REACT_APP_SKIP_LOGIN_MFA === 'true' ||
    process.env.REACT_APP_SKIP_LOGIN_MFA === '1');

export default function ProtectedRoute({ children, allowedRoles }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    sessionUser,
    sessionIsPasswordRecovery,
    role,
    tenantId,
    tenantIsActive,
    tenantName,
    tenantLoading,
    tenantError,
    tenantInvitePending,
    isSuperAdmin,
    loading,
    recovering,
    authNotice,
    signOut,
  } = useAuth();
  // Stale-while-revalidate seed: a cache hit means a PRIOR mount (this tab,
  // this user) already completed a successful MFA check, so we render that
  // last-known-good answer on the very first frame instead of blanking the
  // screen — see mfaGateCache.js. The effect below still runs the full check
  // on every mount regardless; this only avoids blocking the first paint on
  // it. Read once so all three initial values agree with each other.
  const cachedMfaGate = readMfaGate(sessionUser?.id);
  const [mfaChecked, setMfaChecked] = useState(() => cachedMfaGate !== null);
  const [mfaRequired, setMfaRequired] = useState(() => cachedMfaGate?.mfaRequired ?? false);
  const [mfaEnrollmentRequired, setMfaEnrollmentRequired] = useState(() => cachedMfaGate?.mfaEnrollmentRequired ?? false);
  const [mfaRecheckNonce, setMfaRecheckNonce] = useState(0);
  // Held true while the freshly-enrolled user is on the recovery-codes step.
  // Verify elevates the session to AAL2 immediately, so a background checkMfa
  // re-run (tab refocus / session sync) would otherwise find a verified factor
  // and clear the gate BEFORE the codes are saved. This latch keeps the gate up
  // until the user acknowledges them (onEnrolled also clears it).
  const [recoveryHold, setRecoveryHold] = useState(false);

  // KAN-529: re-ask the server mid-session — on an MFA_REQUIRED refusal, on
  // resume/focus, and when the company's 2FA policy changes (a plain RLS
  // denial is a silent `200 []`, so list screens alone would never tell us).
  useMfaServerRecheck({
    sessionUserId: sessionUser?.id,
    onRecheck: () => setMfaRecheckNonce((n) => n + 1),
    paused: mfaRequired || mfaEnrollmentRequired,
  });

  const blocking = loading || recovering || tenantLoading;

  // Signal the singleton splash overlay. No `immediate` flag: the overlay's
  // built-in 180ms flash-protection delay prevents transient recovery blips
  // from ever painting, and real recovery work reveals it smoothly.
  useSplash(blocking || (Boolean(sessionUser) && !mfaChecked));
  // The 2FA-gate check below renders NOTHING until it answers — keep the
  // splash up through it (a fresh sign-in has no cached gate, so it always
  // runs), then hand the sign-in cover off once real content has painted
  // (signInCover.js; 2026-09-26 "blank after login").
  const guardSettled = !blocking && Boolean(sessionUser) && mfaChecked;
  useEffect(() => { if (guardSettled) releaseSignInCoverAfterPaint(); }, [guardSettled]);

  useEffect(() => {
    if (!sessionUser) {
      setMfaChecked(false);
      setMfaRequired(false);
      clearMfaGate();
      return;
    }
    if (blocking) return;

    let isMounted = true;
    // Captured BEFORE the awaits below. A clear that lands while this check
    // is in flight (sign-out, session expiry, a failed sibling check) bumps
    // the generation, and the write at the end is dropped — a stale answer
    // can never be resurrected for a session that has already ended.
    const gateGen = mfaGateGeneration();
    const checkMfa = async () => {
      if (skipLoginMfa) {
        if (isMounted) { setMfaChecked(true); setMfaRequired(false); setMfaEnrollmentRequired(false); }
        return;
      }
      // Run all four fetches in parallel to avoid serial latency.
      // Both MFA fetches FAIL-OPEN: if either errors or returns null, the
      // corresponding arg defaults to [] so getEffectiveMfaRequiredRoles([], [])
      // = empty (nothing forced). This ensures a flaky read never strands any
      // user behind the gate. tenantId may be null for first loads; maybeSingle
      // handles that gracefully.
      const [
        { data: factorsData, error: factorsError },
        { data: aalData, error: aalError },
        { data: csData },
        { data: floorData },
        { data: serverMfa },
      ] = await withTimeout(Promise.all([
        supabase.auth.mfa.listFactors(),
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
        tenantId
          ? supabase.from('company_settings').select('mfa_required_roles').eq('tenant_id', tenantId).maybeSingle()
          : Promise.resolve({ data: null }),
        tenantId
          ? supabase.from('tenant_mfa_policy').select('required_roles_floor').eq('tenant_id', tenantId).maybeSingle()
          : Promise.resolve({ data: null }),
        // KAN-529: the server's own answer (same rule the DB enforces). An
        // error/old DB → { data: null } → falls back to the client rule below.
        fetchServerMfaRequirement(),
      ]), MFA_GATE_TIMEOUT_MS).catch((err) => [{ error: err }, { error: err }, { data: null }, { data: null }, { data: null }]);
      // ↑ A hung check (iOS-PWA suspended request) must not park the splash forever — it times out into the fail-closed branch below.
      if (factorsError || aalError) {
        // Fail closed: if MFA check fails, require MFA (redirect to login for retry).
        // Never force enrollment on a failed factors call — existing redirect handles uncertainty.
        console.error('MFA check failed in ProtectedRoute:', factorsError || aalError);
        // A failed check must never leave a stale OPEN cached answer for the
        // next mount to seed from — clear regardless of isMounted so a
        // background failure (e.g. this mount already unmounted) still
        // invalidates it.
        clearMfaGate();
        if (isMounted) { setMfaRequired(true); setMfaEnrollmentRequired(false); setMfaChecked(true); }
        return;
      }
      const hasVerifiedTotp = (factorsData?.totp ?? []).some((f) => f.status === 'verified');
      const atAal1 = Boolean(aalData?.currentLevel === 'aal1');
      const nextIsAal2 = aalData?.nextLevel === 'aal2';
      // Fail-open: both inputs default to [] if the fetch errored or returned no row.
      const effective = getEffectiveMfaRequiredRoles(
        floorData?.required_roles_floor ?? [],
        csData?.mfa_required_roles ?? [],
      );
      // Next-login enforcement (grandfather active sessions): a genuine fresh
      // sign-in snapshots the effective set as this session's baseline; reloads
      // read it. We force enrollment only for roles required AT sign-in AND still
      // required now — so a mandate added mid-session (incl. the admin backfill)
      // waits for the NEXT login, and a removed mandate releases immediately. A
      // session with no baseline (pre-existing) is grandfathered until it
      // re-authenticates. See mfaEnrollmentBaseline.js.
      const uid = sessionUser?.id;
      const isFreshLogin =
        typeof window !== 'undefined' && window.sessionStorage?.getItem('cv_mfa_fresh_login') === '1';
      let baseline;
      if (isFreshLogin) {
        writeEnrollmentBaseline(uid, [...effective]);
        try { window.sessionStorage.removeItem('cv_mfa_fresh_login'); } catch (_) { /* private mode */ }
        baseline = [...effective];
      } else {
        baseline = readEnrollmentBaseline(uid);
      }
      const enforceable = enforceableRolesAtSession(baseline, effective);
      const nextMfaRequired = atAal1 && (nextIsAal2 || hasVerifiedTotp);
      // KAN-529: the server refuses a 2FA-required account with no factor (no
      // next-login grace beyond sessions that predate the server floor), so
      // when it says enrollment is required the gate shows — whatever the
      // client baseline above concluded.
      const serverEnrollmentRequired = serverMfa?.enrollment_required === true && !hasVerifiedTotp;
      const nextMfaEnrollmentRequired =
        shouldForceMfaEnrollment(role, hasVerifiedTotp, enforceable) || serverEnrollmentRequired;
      if (isMounted) {
        setMfaRequired(nextMfaRequired);
        setMfaEnrollmentRequired(nextMfaEnrollmentRequired);
        setMfaChecked(true);
      }
      // Cache the successful answer regardless of isMounted — a background
      // refresh that resolves after this mount already left should still
      // update the seed for the NEXT mount (stale-while-revalidate).
      writeMfaGate(sessionUser?.id, {
        mfaRequired: nextMfaRequired,
        mfaEnrollmentRequired: nextMfaEnrollmentRequired,
      }, gateGen);
    };
    void checkMfa();
    return () => { isMounted = false; };
  // sessionUser?.id used as identity key; sessionUser itself needed for the null guard
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionUser?.id, blocking, role, mfaRecheckNonce]);

  const handleInactiveTenantSignOut = async () => {
    await signOut();
    navigate('/');
  };

  if (blocking) {
    // The singleton splash overlay (signaled by useSplash above) is already
    // covering the screen. Return null so guarded children stay out of the
    // tree until auth/tenant resolve.
    return null;
  }

  if (!sessionUser) {
    return <Navigate to="/" replace />;
  }

  if (sessionIsPasswordRecovery) {
    return <Navigate to="/reset-password" replace />;
  }

  if (!mfaChecked) return null;
  if (mfaRequired) return <Navigate to="/" replace />;

  // Mid-invite (signed in, password not set yet): the accept-invite page decides
  // the 'form' step for this state and offers "Not you? Sign out".
  if (tenantInvitePending) return <Navigate to="/accept-invite" replace />;

  // Access-status screens (deactivated member / inactive company / other
  // tenant-resolution error) — presentation only. Variant selection mirrors
  // the text choice the old hand-rolled blocks made (never a new gating
  // condition): a tenantError means tenant resolution itself failed (no
  // deactivated-membership-specific error id exists today — every tenantError
  // is a load failure, so it always maps to 'generic', body = that error's
  // own message, exactly as before); no tenantError and no tenantId means the
  // membership resolved but came back empty, which is what a deactivated
  // member's membership row looks like ('account', same case that rendered
  // auth.accountDeactivated before); tenantIsActive === false is the company
  // case ('company').
  //
  // P1 (2026-09-24): tenantIsActive === false can now ALSO arrive here (no
  // tenantId, no tenantError) — syncSession's get_my_access_status lookup
  // set it that way on purpose when the caller's OWN company (not their own
  // membership) is the deactivated party, and it never sets tenantId, so
  // this branch (not the tenantId-requiring one below) is the one that has
  // to catch it. tenantError still wins first: a real tenant-RESOLUTION
  // failure is a different story than a resolved-and-inactive company, even
  // though both currently leave tenantId null.
  if (tenantError || !tenantId) {
    if (!tenantError && tenantIsActive === false) {
      return (
        <AccessStatusScreen
          variant="company"
          email={sessionUser?.email}
          bodyOverride={tenantName
            ? intl.formatMessage(
                {
                  id: 'auth.access.company.bodyNamed',
                  defaultMessage: '{tenantName}’s account is inactive, so no one can sign in right now. Contact your administrator.',
                },
                { tenantName },
              )
            : null}
          onSignOut={handleInactiveTenantSignOut}
        />
      );
    }
    return (
      <AccessStatusScreen
        variant={tenantError ? 'generic' : 'account'}
        email={sessionUser?.email}
        message={tenantError ? intl.formatMessage({ id: tenantError, defaultMessage: tenantError }) : null}
        onSignOut={handleInactiveTenantSignOut}
      />
    );
  }

  if (sessionUser && tenantId && tenantIsActive === false) {
    return (
      <AccessStatusScreen
        variant="company"
        email={sessionUser?.email}
        onSignOut={handleInactiveTenantSignOut}
      />
    );
  }

  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to="/" replace />;
  }

  // RD-SEC-008: admins with no verified TOTP factor must enroll before the app
  // renders. This is a UX/onboarding gate (client-side), NOT the security
  // boundary — the real teeth are the server-side AAL2 gates (requireAal2IfEnrolled
  // on the destructive tenant edge functions), which become an effective hard
  // requirement once every admin is enrolled. Enrolling elevates the session to
  // AAL2; onEnrolled bumps the recheck nonce, the gate clears, children render.
  if (mfaEnrollmentRequired || recoveryHold) {
    return (
      <TenantMfaEnrollmentGate
        onEnrolled={() => { setRecoveryHold(false); setMfaRecheckNonce((n) => n + 1); }}
        onSignOut={handleInactiveTenantSignOut}
        onRecoveryHoldChange={setRecoveryHold}
      />
    );
  }

  return (
    <>
      {authNotice?.type === 'info' && authNotice?.message && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 'calc(8px + env(safe-area-inset-bottom, 0px)) 14px 8px 14px',
            backgroundColor: semantic.bg.inverseGlass,
            color: semantic.fg.inverseSoft,
            fontSize: '0.8125rem',
            textAlign: 'center',
            zIndex: 12000,
            pointerEvents: 'none',
          }}
        >
          {authNotice.message}
        </div>
      )}
      <OpenPlanBillingHandler />
      {/* KAN-204: a suspended company never reaches the app (platform super-admins are exempt). */}
      {isSuperAdmin ? children : (
        <SuspendedAccessGate email={sessionUser?.email} onSignOut={handleInactiveTenantSignOut}>
          {children}
        </SuspendedAccessGate>
      )}
    </>
  );
}
